import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SidebarNavMenuItem } from '../../../../decorators/sidebar-nav-menu-item';
import { PolicyTemplateService } from '../../../../services/entity-services/policy-template.service';
import { PolicyTemplateSummary } from '../../../../interfaces/policy-templates/policy-template-summary.interface';
import { PolicyTemplateDetail } from '../../../../interfaces/policy-templates/policy-template-detail.interface';
import { Log, IShortLogger } from '../../../../services/logger.service';
import { UserPrefsService } from '../../../../services/user-prefs.service';
import { AuthService } from '../../../../services/auth.service';
import { isParticipant } from '../../../../utils/auth.utils';
import { ConfirmDialogService } from '../../../../services/confirm-dialog.service';
import { NotificationService } from '../../../../services/notification.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../shared/pagination-options';

/** UserPrefs page key for the policy template info banner. */
const BANNER_PAGE = 'policies';

@SidebarNavMenuItem({
  route: '/policy-template',
  faIcon: 'policy',
  name: 'Policy Template',
  order: 1300,
  businessProcessesName: 'Policy Template'
})
@Component({
  selector: 'app-policy-template-list',
  templateUrl: './policy-template-list.component.html',
  styleUrl: './policy-template-list.component.css'
})
export class PolicyTemplateListComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<PolicyTemplateListComponent> = Log.create(PolicyTemplateListComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  allPolicies: PolicyTemplateSummary[] = [];
  dataSource = new MatTableDataSource<PolicyTemplateSummary>([]);
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;
  /** `true` when the current user is a management-team admin and may archive entities. */
  get canArchive(): boolean { return this.authService.canArchive(); }
  /** Active table columns; 'actions' is included only for management-team admins. */
  get displayedColumns(): string[] {
    const base = ['resourceType', 'policyName', 'description', 'updatedAt'];
    return this.canArchive ? [...base, 'actions'] : base;
  }

  searchTerm = '';
  /** Independent toggle chips — both off or both on = show all. */
  vmChipActive = false;
  uaChipActive = false;

  loading = false;
  error: string | null = null;
  showDetailDrawer = false;
  selectedDetail: PolicyTemplateDetail | null = null;
  loadingDetail = false;

  // ── Banner state ──────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  constructor(
    private readonly policyTemplateService: PolicyTemplateService,
    private readonly userPrefs: UserPrefsService,
    private readonly authService: AuthService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly notification: NotificationService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadPolicies();

    // Show banner only for operator users who have not permanently dismissed it.
    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(BANNER_PAGE);

    this.dataSource.filterPredicate = (row: PolicyTemplateSummary, filter: string) =>
      (row.policyName?.toLowerCase().includes(filter) ?? false)
      || (row.description?.toLowerCase().includes(filter) ?? false);

    this.dataSource.sortingDataAccessor = (row: PolicyTemplateSummary, sortHeaderId: string) => {
      if (sortHeaderId === 'policyName')   return row.policyName?.toLowerCase() ?? '';
      if (sortHeaderId === 'description')  return row.description?.toLowerCase() ?? '';
      if (sortHeaderId === 'resourceType') return row.resourceType?.toLowerCase() ?? '';
      if (sortHeaderId === 'updatedAt')    return row.updatedAt ?? '';
      return '';
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /** Hides the info banner; if "Don't show again" is checked, persists the dismissal. */
  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(BANNER_PAGE);
    }
  }

  loadPolicies(): void {
    this.loading = true;
    this.error = null;
    this.policyTemplateService.getAll().subscribe({
      next: (policies) => {
        this.allPolicies = policies ?? [];
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load policy templates. Please try again.';
        this.loading = false;
        this.logger.e('Error loading policy templates:', err);
      }
    });
  }

  toggleVmChip(): void {
    this.vmChipActive = !this.vmChipActive;
    this.applyFilters();
  }

  toggleUaChip(): void {
    this.uaChipActive = !this.uaChipActive;
    this.applyFilters();
  }

  private applyFilters(): void {
    const bothOff = !this.vmChipActive && !this.uaChipActive;
    const bothOn  =  this.vmChipActive &&  this.uaChipActive;

    this.dataSource.data = (bothOff || bothOn)
      ? this.allPolicies
      : this.allPolicies.filter(p =>
          (this.vmChipActive && p.resourceType === 'Vm') ||
          (this.uaChipActive && p.resourceType === 'UserAccount')
        );

    this.applySearchFilter();
  }

  applySearchFilter(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onSearchChange(): void {
    this.applySearchFilter();
  }

  onRowClick(policy: PolicyTemplateSummary): void {
    this.viewDetail(policy);
  }

  viewDetail(policy: PolicyTemplateSummary): void {
    this.loadingDetail = true;
    this.selectedDetail = null;
    this.showDetailDrawer = true;
    this.policyTemplateService.getById(policy.id).subscribe({
      next: (detail) => {
        this.selectedDetail = detail;
        this.loadingDetail = false;
      },
      error: (err) => {
        this.logger.e('Error loading policy detail:', err);
        this.error = 'Failed to load policy details.';
        this.loadingDetail = false;
        this.showDetailDrawer = false;
      }
    });
  }

  closeDetailDrawer(): void {
    this.showDetailDrawer = false;
    this.selectedDetail = null;
    this.loadingDetail = false;
  }

  getResourceTypeIcon(resourceType: string): string {
    if (resourceType === 'Vm') return 'computer';
    if (resourceType === 'UserAccount') return 'manage_accounts';
    return 'settings';
  }

  formatCleanupAction(action: any): string {
    if (!action) return '—';
    if (action.actionType === 'DoNothing') return 'Do Nothing';
    const gpv = action.gracePeriod?.value;
    const gpu = action.gracePeriod?.unit;
    const gpStr = gpv && gpu ? ` (grace: ${gpv} ${gpu})` : '';
    if (action.actionType === 'Shutdown')   return `Shutdown${gpStr}`;
    if (action.actionType === 'Deallocate') return `Deallocate${gpStr}`;
    if (action.actionType === 'Destroy') {
      const scope = action.scope === 'ComputeOnly' ? 'compute only' : 'compute + disk';
      return `Destroy — ${scope}${gpStr}`;
    }
    if (action.actionType === 'DeactivateSubscription') return 'Deactivate Subscription';
    if (action.actionType === 'DeleteResources') return 'Delete Resources';
    return action.actionType ?? '—';
  }

  formatQuotaLimit(quota: any): string {
    if (!quota) return 'Not configured';
    const threshold = quota.thresholdAmount != null ? `₹${quota.thresholdAmount}` : '—';
    const action = quota.action?.actionType ?? '—';
    return `${threshold} — ${action}`;
  }

  get hasActiveFilter(): boolean {
    return this.vmChipActive || this.uaChipActive;
  }

  // ── Archive ───────────────────────────────────────────────────────────────

  /**
   * Prompts the user then archives the policy template.
   * Returns a 409 if linked classes are still active — shows a user-friendly message.
   */
  archiveTemplate(row: PolicyTemplateSummary): void {
    this.confirmDialog.confirmAction(
      'Archive Policy Template',
      `Archive "${row.policyName}"? Archived templates are hidden from the list.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.policyTemplateService.archive(row.id).subscribe({
        next: () => {
          this.notification.notify(`Policy template "${row.policyName}" archived.`, 'success', 3000);
          this.loadPolicies();
        },
        error: (err: any) => {
          if (err?.status === 409) {
            this.snackBar.open(
              'Cannot archive: some linked classes are still active. Archive them first.',
              'Dismiss', { duration: 6000 }
            );
          } else {
            this.notification.notify('Failed to archive policy template.', 'error', 3000);
          }
        }
      });
    });
  }
}
